import React from 'react';
import { motion } from 'framer-motion';
import { Code, Server, Users } from 'lucide-react';

interface CalculatorBreakdownProps {
  results: {
    breakdown: {
      devEnergy_kWh: number;
      devCO2_kg: number;
      infraEnergy_kWh: number;
      infraCO2_kg: number;
      userEnergy_kWh: number;
      userCO2_kg: number;
    };
  };
  state: {
    devRegion: string;
    infraRegion: string;
    userRegion: string;
  };
}

const regions = [
  { name: "France", factor: 0.05, countries: ["France"] },
  { name: "Scandinavie", factor: 0.05, countries: ["Suède", "Norvège", "Danemark", "Finlande"] },
  { name: "Europe de l'Ouest", factor: 0.20, countries: ["Allemagne", "Belgique", "Pays-Bas", "Luxembourg", "Suisse"] },
  { name: "Europe de l'Est", factor: 0.60, countries: ["Pologne", "Roumanie", "Hongrie", "Tchéquie", "Slovaquie"] },
  { name: "Amérique du Nord", factor: 0.40, countries: ["États-Unis", "Canada"] },
  { name: "Amérique du Sud", factor: 0.25, countries: ["Brésil", "Argentine", "Chili", "Colombie"] },
  { name: "Amérique Centrale", factor: 0.30, countries: ["Mexique", "Costa Rica", "Panama"] },
  { name: "Afrique du Nord", factor: 0.45, countries: ["Maroc", "Tunisie", "Algérie", "Égypte"] },
  { name: "Afrique Sub-Saharienne", factor: 0.55, countries: ["Sénégal", "Côte d'Ivoire", "Kenya", "Afrique du Sud"] },
  { name: "Asie du Nord", factor: 0.65, countries: ["Chine", "Japon", "Corée du Sud"] },
  { name: "Asie du Sud", factor: 0.75, countries: ["Inde", "Pakistan", "Bangladesh"] },
  { name: "Asie du Sud-Est", factor: 0.60, countries: ["Thaïlande", "Vietnam", "Indonésie", "Malaisie"] },
  { name: "Océanie", factor: 0.50, countries: ["Australie", "Nouvelle-Zélande"] },
  { name: "Moyen-Orient", factor: 0.55, countries: ["Émirats Arabes Unis", "Arabie Saoudite", "Israël"] },
  { name: "Moyenne Mondiale", factor: 0.45, countries: ["Moyenne mondiale"] }
];

const CarbonCalculatorBreakdown: React.FC<CalculatorBreakdownProps> = ({ results, state }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="bg-white p-6 rounded-xl shadow-lg border border-slate-200"
    >
      <h3 className="text-xl font-semibold text-slate-900 mb-6">Détail de l'impact</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-slate-50">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-full bg-blue-500/10">
              <Code className="h-5 w-5 text-blue-500" />
            </div>
            <h4 className="font-semibold text-slate-900">Développement</h4>
          </div>
          <div className="pl-9 space-y-1">
            <p className="text-sm text-slate-600">
              {results.breakdown.devEnergy_kWh.toLocaleString()} kWh
            </p>
            <p className="text-sm text-slate-600">
              {(results.breakdown.devCO2_kg/1000).toFixed(4)} t CO₂e
            </p>
            <p className="text-xs text-slate-500">
              {regions.find(r => r.name === state.devRegion)?.factor || 0.05} kg CO₂e/kWh ({state.devRegion})
            </p>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-slate-50">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-full bg-purple-500/10">
              <Server className="h-5 w-5 text-purple-500" />
            </div>
            <h4 className="font-semibold text-slate-900">Infrastructure</h4>
          </div>
          <div className="pl-9 space-y-1">
            <p className="text-sm text-slate-600">
              {results.breakdown.infraEnergy_kWh.toLocaleString()} kWh
            </p>
            <p className="text-sm text-slate-600">
              {(results.breakdown.infraCO2_kg/1000).toFixed(4)} t CO₂e
            </p>
            <p className="text-xs text-slate-500">
              {regions.find(r => r.name === state.infraRegion)?.factor || 0.05} kg CO₂e/kWh ({state.infraRegion})
            </p>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-slate-50">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-full bg-amber-500/10">
              <Users className="h-5 w-5 text-amber-500" />
            </div>
            <h4 className="font-semibold text-slate-900">Utilisateurs</h4>
          </div>
          <div className="pl-9 space-y-1">
            <p className="text-sm text-slate-600">
              {results.breakdown.userEnergy_kWh.toLocaleString()} kWh
            </p>
            <p className="text-sm text-slate-600">
              {(results.breakdown.userCO2_kg/1000).toFixed(4)} t CO₂e
            </p>
            <p className="text-xs text-slate-500">
              {regions.find(r => r.name === state.userRegion)?.factor || 0.45} kg CO₂e/kWh ({state.userRegion})
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CarbonCalculatorBreakdown; 